import { IFormController, IPromise, IQService } from 'angular';
import { LeadsPolicyService } from '../../../core/services';
import { UtilityService } from '../../../core/services/utility.service';
import { LeadsPolicyVM, mapLeadsPolicySaveRequest, mapLeadsPolicyToVM } from '../../leads/policy/leads-policy-mapper';
import { defaultPin } from '../../leads/policy/marker-icons';
import templateUrl from './edit-division-leads-policy-container.component.html';


/**
 * A component that provides service integration in order to edit a division's leads policy.
 */
export const EditDivisionLeadsPolicyContainerComponent = {
    templateUrl,
    bindings: {
        division: "<"
    },
    controller: class EditDivisionLeadsPolicyContainer {
        division: any;
        leadsPolicy: Partial<LeadsPolicyVM>;
        leadsPolicyEditForm: IFormController;
        constructor(private $q: IQService, private leadsPolicyService: LeadsPolicyService,
            private utilityService: UtilityService) {
            'ngInject'
        }

        $onInit() {
            // Load and display the division's leads policy, if it has one.
            let loading: IPromise<unknown> = this.$q.resolve()
            if (this.division && this.division.LeadsPolicyID) {
                loading = this.getLeadsPolicyVM(this.division.DivisionID)
                    .then(policy => this.leadsPolicy = policy)
            }

            this.utilityService.showLoadingUntilResolved(loading)
        }

        $onChanges() {
            // Load and display the division's leads policy, if it has one.
            let loading: IPromise<unknown> = this.$q.resolve()
            if (this.division && this.division.LeadsPolicyID) {
                loading = this.getLeadsPolicyVM(this.division.DivisionID)
                    .then(policy => this.leadsPolicy = policy)
            }

            this.utilityService.showLoadingUntilResolved(loading)
        }

        onAddLeadsPolicy(division) {
            this.leadsPolicy = {
                name: division.Name,
                leadStatuses: [],
                markerIcon: defaultPin,
                minClusterZoom: null,
                // Note: these defaults are based on optimal settings for Rogers.
                maxClusterZoom: 17,
                minClickZoom: 18,
                minSpiderZoom: 19,
                importInGroups: false
            }
        }

        onAddLeadStatus({ leadStatus }) {
            this.leadsPolicy.leadStatuses.push(leadStatus)
            if (this.division.LeadsPolicyID) {
                const request = mapLeadsPolicySaveRequest(this.leadsPolicy);

                const saved = this.leadsPolicyService.updateLeadsPolicy(this.division.LeadsPolicyID, request)
                // Reload the policy to ensure the new lead status has an ID.
                const reloaded = saved
                    .then(() => this.getLeadsPolicyVM(this.division.DivisionID))
                    .then(policy => this.leadsPolicy = policy)

                // Perform the update in-place.
                this.utilityService.showLoadingUntilResolved(
                    this.utilityService.alertOnError(saved, "Saving failed")
                )
                this.utilityService.showLoadingUntilResolved(reloaded)
            }
        }

        onDeleteLeadStatus({ leadStatus, index }) {
            if (leadStatus.isDefault) {
                alert("The default status cannot be deleted. Please make another status the default before removing this status.")
                return
            }

            if (!confirm("The status will be deleted. It will still appear in the status history, but it will no longer be assignable")) {
                return
            }

            // Perform the delete in memory immediately.
            this.leadsPolicy.leadStatuses = this.leadsPolicy.leadStatuses
                .filter((_, i) => i !== index)
            if (this.division.LeadsPolicyID) {
                this.saveLeadsPolicyUpdate()
            }
        }

        onChangeLeadStatus({ leadStatus, index }) {
            // Update the lead status in the list based on the status on the page.
            const currentLeadStatus = this.leadsPolicy.leadStatuses[index]
            currentLeadStatus.name = leadStatus.name
            currentLeadStatus.color = leadStatus.color
            currentLeadStatus.hue = leadStatus.hue
            currentLeadStatus.isDefault = leadStatus.isDefault
            currentLeadStatus.leadStatusType = leadStatus.leadStatusType

            if (this.division.LeadsPolicyID) {
                // Only save in-place for existing leads policy.
                this.saveLeadsPolicyUpdate()
            }
        }

        onSave() {
            if (this.leadsPolicyEditForm && this.leadsPolicyEditForm.$invalid) {
                // The invalid component will warn about its invalid state.
                return
            }

            const request = mapLeadsPolicySaveRequest(this.leadsPolicy)
            const saved = this.leadsPolicyService.defineDivisionLeadsPolicy(this.division.DivisionID, request)

            // Reload in order to ensure all child entities have their IDs set.
            const reloaded = saved
                .then(id => {
                    this.division.LeadsPolicyID = id
                    return this.getLeadsPolicyVM(this.division.DivisionID)
                })
                .then(policy => this.leadsPolicy = policy)

            // Perform the update in-place.
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(saved, "Saving failed")
            )
            this.utilityService.showLoadingUntilResolved(reloaded)
        }

        onCancel() {
            this.leadsPolicy = null
        }

        onRemove() {
            if (confirm("The leads policy will be removed. Is this ok?")) {
                this.leadsPolicyService.removeDivisionLeadsPolicy(this.division.DivisionID)
                    .then(() => {
                        this.division.LeadsPolicyID = null
                        this.leadsPolicy = null
                    })
            }
        }

        onChange() {
            if (!this.division.LeadsPolicyID) {
                return; // Only save when submitting the form.
            }
            this.saveLeadsPolicyUpdate();
        }

        /**
         * Get a Leads Policy VM for the division's leads policy data.
         * @param {number} divisionID The division ID
         */
        getLeadsPolicyVM(divisionID) {
            return this.leadsPolicyService.retrieveDivisionLeadsPolicy(divisionID)
                .then(leadsPolicy => {
                    if (!leadsPolicy) return
                    return mapLeadsPolicyToVM(leadsPolicy)
                })
        }

        saveLeadsPolicyUpdate() {
            if (this.leadsPolicyEditForm && this.leadsPolicyEditForm.$invalid) {
                // The invalid component will warn about its invalid state.
                return
            }

            const request = mapLeadsPolicySaveRequest(this.leadsPolicy);
            // Perform the update in-place.
            const saved = this.leadsPolicyService.updateLeadsPolicy(this.division.LeadsPolicyID, request);
            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(saved, "Saving failed")
            )
        }
    }
}
